/**
 * A module's assignments, for its mentor: ZIPs, handed to students as they
 * are. Several per module; edit the details, upload a new version of the ZIP,
 * download, delete.
 *
 * kind "topic": a 1:1 student's module. kind "template": a cohort's module,
 * shared by the whole batch.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";

import * as tasksApi from "./moduleTasksApi";

const FONT = "Be Vietnam Pro";
const BLUE = "#0172A6";
const DIFFICULTIES = [["E", "Easy"], ["M", "Medium"], ["H", "Hard"]];
const DIFFICULTY_LABEL = Object.fromEntries(DIFFICULTIES);
const MAX_MB = 200;

const sizeLabel = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const checkZip = (file) => {
  if (!file.name.toLowerCase().endsWith(".zip")) return "Choose a .zip file.";
  if (file.size > MAX_MB * 1024 * 1024) return `The ZIP must be at most ${MAX_MB} MB.`;
  return "";
};

/** Title, description, difficulty: for a new assignment and when editing one. */
const DetailsFields = ({ value, onChange }) => (
  <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
    <TextField
      size="small" label="Title" value={value.title} sx={{ flex: "2 1 220px" }} inputProps={{ maxLength: 255 }}
      onChange={(e) => onChange({ ...value, title: e.target.value })}
    />
    <TextField
      size="small" select label="Difficulty" value={value.difficulty} sx={{ flex: "0 1 130px" }}
      onChange={(e) => onChange({ ...value, difficulty: e.target.value })}
    >
      {DIFFICULTIES.map(([v, label]) => <MenuItem key={v} value={v}>{label}</MenuItem>)}
    </TextField>
    <TextField
      size="small" label="Description (optional)" value={value.description} multiline maxRows={4}
      sx={{ flex: "1 1 100%" }} onChange={(e) => onChange({ ...value, description: e.target.value })}
    />
  </Box>
);

const AssignmentsManager = ({ open, onClose, kind, moduleId, moduleName }) => {
  const [items, setItems] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [toast, setToast] = useState(null);
  const [adding, setAdding] = useState(null);       // { file, title, description, difficulty }
  const [editing, setEditing] = useState(null);     // { id, title, description, difficulty }
  const [confirming, setConfirming] = useState(null);
  const [progress, setProgress] = useState(null);   // upload % while a ZIP goes up
  const [busy, setBusy] = useState(false);
  const addInput = useRef(null);
  const replaceInput = useRef(null);
  const replacing = useRef(null);

  const notify = (message, severity = "success") => setToast({ message, severity });

  const load = useCallback(async () => {
    if (!moduleId) return;
    setLoadError("");
    try {
      setItems((await tasksApi.listAssignments(kind, moduleId)).assignments);
    } catch (e) {
      setLoadError(tasksApi.errorMessage(e, "Couldn't load the assignments."));
    }
  }, [kind, moduleId]);

  useEffect(() => {
    if (open) {
      setItems(null);
      setAdding(null);
      setEditing(null);
      load();
    }
  }, [open, load]);

  const onPickNew = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const problem = checkZip(file);
    if (problem) return notify(problem, "error");
    setAdding({ file, title: file.name.replace(/\.zip$/i, ""), description: "", difficulty: "M" });
  };

  const saveNew = async () => {
    setBusy(true);
    setProgress(0);
    try {
      const uploaded = await tasksApi.uploadZip(kind, moduleId, adding.file, setProgress);
      await tasksApi.createAssignment(kind, moduleId, {
        ...uploaded, title: adding.title.trim(), description: adding.description, difficulty: adding.difficulty,
      });
      notify(`“${adding.title.trim()}” added.`);
      setAdding(null);
      load();
    } catch (e) {
      notify(tasksApi.errorMessage(e, "The upload failed."), "error");
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const { id, title, description, difficulty } = editing;
      await tasksApi.updateAssignment(kind, moduleId, id, { title: title.trim(), description, difficulty });
      setEditing(null);
      notify("Saved.");
      load();
    } catch (e) {
      notify(tasksApi.errorMessage(e, "Couldn't save."), "error");
    } finally {
      setBusy(false);
    }
  };

  const onPickReplacement = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    const item = replacing.current;
    if (!file || !item) return;
    const problem = checkZip(file);
    if (problem) return notify(problem, "error");
    setBusy(true);
    setProgress(0);
    try {
      const uploaded = await tasksApi.uploadZip(kind, moduleId, file, setProgress);
      const { assignment } = await tasksApi.updateAssignment(kind, moduleId, item.id, uploaded);
      notify(`“${assignment.title}” is now version ${assignment.current_version}.`);
      load();
    } catch (e) {
      notify(tasksApi.errorMessage(e, "The upload failed."), "error");
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const download = async (item) => {
    try {
      const { download_url } = await tasksApi.assignmentDownloadUrl(item.id);
      if (download_url) window.open(download_url, "_blank", "noopener");
    } catch (e) {
      notify(tasksApi.errorMessage(e, "Couldn't download."), "error");
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await tasksApi.deleteAssignment(kind, moduleId, confirming.id);
      notify(`“${confirming.title}” deleted.`);
      setConfirming(null);
      load();
    } catch (e) {
      notify(tasksApi.errorMessage(e, "Couldn't delete."), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontFamily: FONT }}>
        <Box sx={{ flex: 1 }}>
          Assignments
          <Typography sx={{ fontSize: 13, color: "#6b7280" }}>
            {moduleName || "Module"}{kind === "template" ? " · every student in the batch" : ""}
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={() => onClose()} disabled={busy}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ background: "#fbfdff" }}>
        <input ref={addInput} type="file" accept=".zip,application/zip" hidden onChange={onPickNew} data-testid="zip-input" />
        <input ref={replaceInput} type="file" accept=".zip,application/zip" hidden onChange={onPickReplacement} />
        {progress !== null && <LinearProgress variant="determinate" value={progress} sx={{ mb: 2 }} />}

        {adding ? (
          <Box sx={{ border: "1px solid #d6e6f2", borderRadius: "10px", p: 2, mb: 2, background: "#fff" }}>
            <Typography sx={{ fontWeight: 600, mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
              <FolderZipOutlinedIcon fontSize="small" /> {adding.file.name}
              <Typography component="span" sx={{ fontSize: 12, color: "#6b7280" }}>{sizeLabel(adding.file.size)}</Typography>
            </Typography>
            <DetailsFields value={adding} onChange={setAdding} />
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
              <Button onClick={() => setAdding(null)} disabled={busy} sx={{ textTransform: "none" }}>Cancel</Button>
              <Button
                variant="contained" onClick={saveNew} disabled={busy || !adding.title.trim()}
                startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
                sx={{ textTransform: "none", backgroundColor: BLUE }}
              >
                Upload assignment
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
            <Typography sx={{ flex: 1, fontWeight: 600, fontFamily: FONT }}>
              {items === null ? "" : items.length ? `${items.length} assignment${items.length === 1 ? "" : "s"}` : "No assignments yet"}
            </Typography>
            <Button
              variant="contained" startIcon={<FileUploadOutlinedIcon />} disabled={busy}
              onClick={() => addInput.current?.click()} sx={{ textTransform: "none", backgroundColor: BLUE }}
            >
              Upload ZIP
            </Button>
          </Box>
        )}

        {loadError && <Alert severity="error" action={<Button onClick={load}>Retry</Button>}>{loadError}</Alert>}
        {items === null && !loadError && <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>}

        {items?.map((item) => (
          <Box key={item.id} data-testid="assignment-card"
               sx={{ border: "1px solid #d6e6f2", borderRadius: "10px", p: 1.5, mb: 1.5, background: "#fff" }}>
            {editing?.id === item.id ? (
              <>
                <DetailsFields value={editing} onChange={setEditing} />
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1.5 }}>
                  <Button onClick={() => setEditing(null)} sx={{ textTransform: "none" }}>Cancel</Button>
                  <Button variant="contained" onClick={saveEdit} disabled={busy || !editing.title.trim()}
                          sx={{ textTransform: "none", backgroundColor: BLUE }}>Save</Button>
                </Box>
              </>
            ) : (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <FolderZipOutlinedIcon sx={{ color: BLUE, mt: 0.25 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 15, fontFamily: FONT }}>{item.title}</Typography>
                  {item.description && <Typography sx={{ fontSize: 13, color: "#4b5563" }}>{item.description}</Typography>}
                  <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.5 }}>
                    {item.file_name} · {sizeLabel(item.file_size)} · v{item.current_version}
                    {" · "}{DIFFICULTY_LABEL[item.difficulty] || "Medium"}
                    {item.submission_count > 0 && ` · ${item.submission_count} submitted`}
                  </Typography>
                </Box>
                <Tooltip title="Download"><IconButton size="small" aria-label="Download" onClick={() => download(item)}><DownloadOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Edit details">
                  <IconButton size="small" aria-label="Edit" disabled={busy}
                              onClick={() => setEditing({ id: item.id, title: item.title, description: item.description, difficulty: item.difficulty })}>
                    <DriveFileRenameOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Upload a new version of the ZIP">
                  <IconButton size="small" aria-label="Replace ZIP" disabled={busy}
                              onClick={() => { replacing.current = item; replaceInput.current?.click(); }}>
                    <SwapHorizIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete"><IconButton size="small" aria-label="Delete" disabled={busy} onClick={() => setConfirming(item)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
              </Box>
            )}
          </Box>
        ))}
      </DialogContent>

      <Dialog open={Boolean(confirming)} onClose={() => setConfirming(null)}>
        <DialogTitle>Delete “{confirming?.title}”?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            It will be removed for {kind === "template" ? "every student in the batch" : "the student"}.
            {confirming?.submission_count > 0 && " Submissions students already made are kept."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button color="error" variant="contained" onClick={remove} disabled={busy} sx={{ textTransform: "none" }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : <span />}
      </Snackbar>
    </Dialog>
  );
};

export default AssignmentsManager;
